// The customer's AWS accounts tab (was Installations): the proxy installations in its AWS
// accounts — health, heartbeat, plugin version — with register, and per installation (its row
// opens a summary) rotate, promote and revoke, and
// links to the Installer's linked AWS accounts for the customer (SCR-015, TASK-122) for a session
// that can run it. Never renders a secret outside the one-time dialog (API-007).
import { useEffect, useState } from "preact/hooks";
import { useApi } from "@nexus/ui";
import { ApiError } from "@nexus/ui";
import { ConfirmDialog, Dialog } from "@nexus/ui";
import { errorId, Field } from "@nexus/ui";
import { hasErrors, type FieldErrors } from "@nexus/ui";
import { Icon } from "@nexus/ui";
import { Card, Chip, CopyId } from "@nexus/ui";
import { Link } from "../../ui/shell";
import { RowDetail } from "@nexus/ui";
import { DataTable, type Column } from "@nexus/ui";
import {
  canPromoteInstallationSecret,
  canRevokeInstallation,
  canRotateInstallationSecret,
  dayLabel,
  HEALTH_CHIPS,
  installationFormServerError,
  installationHealth,
  installerAccountPath,
  newInstallationRequest,
  validateNewInstallation,
  type Installation,
  type NewInstallationValues,
} from "./model";
import { openSecretDialog, SecretDialog, type SecretDialogState } from "@nexus/ui";

const EMPTY_FORM: NewInstallationValues = { name: "", awsAccountId: "", awsRegion: "" };

function unexpected(error: unknown): ApiError {
  return error instanceof ApiError ? error : new ApiError(0, "internal", "Something went wrong.");
}

export function AwsAccountsTab({ orgId, canInstall }: { orgId: string; canInstall: boolean }) {
  const client = useApi();
  const [installations, setInstallations] = useState<Installation[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<NewInstallationValues>(EMPTY_FORM);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [secret, setSecret] = useState<SecretDialogState | null>(null);
  const [revoking, setRevoking] = useState<Installation | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const load = (after: string | null, replace: boolean) => {
    setLoading(true);
    setError(null);
    client
      .get("/v1/orgs/{orgId}/installations", { path: { orgId }, query: after ? { cursor: after } : {} })
      .then((page) => {
        setInstallations((current) => (replace ? page.items : [...current, ...page.items]));
        setCursor(page.nextCursor);
      })
      .catch((err: unknown) => setError(unexpected(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(null, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, orgId]);

  const closeDialog = () => {
    setOpen(false);
    setForm(EMPTY_FORM);
    setErrors({});
  };

  const submit = () => {
    const validation = validateNewInstallation(form);
    if (hasErrors(validation)) {
      setErrors(validation);
      return;
    }
    const body = newInstallationRequest(form);
    if (!body) return;
    setSubmitting(true);
    client
      .post("/v1/orgs/{orgId}/installations", { path: { orgId }, body })
      .then((installation) => {
        setInstallations((current) => [installation, ...current]);
        setSecret(openSecretDialog(installation.secret));
        closeDialog();
      })
      .catch((err: unknown) => setErrors(installationFormServerError(unexpected(err))))
      .finally(() => setSubmitting(false));
  };

  const rotate = (installation: Installation) => {
    setBusyId(installation.id);
    client
      .post("/v1/orgs/{orgId}/installations/{installationId}/rotate-secret", { path: { orgId, installationId: installation.id } })
      .then((updated) => {
        setInstallations((current) => current.map((row) => (row.id === updated.id ? updated : row)));
        setSecret(openSecretDialog(updated.secret));
      })
      .catch((err: unknown) => setError(unexpected(err)))
      .finally(() => setBusyId(null));
  };

  const promote = (installation: Installation) => {
    setBusyId(installation.id);
    client
      .post("/v1/orgs/{orgId}/installations/{installationId}/promote-secret", { path: { orgId, installationId: installation.id } })
      .then((updated) => setInstallations((current) => current.map((row) => (row.id === updated.id ? updated : row))))
      .catch((err: unknown) => setError(unexpected(err)))
      .finally(() => setBusyId(null));
  };

  const confirmRevoke = () => {
    if (!revoking) return;
    const target = revoking;
    setBusyId(target.id);
    client
      .delete("/v1/orgs/{orgId}/installations/{installationId}", { path: { orgId, installationId: target.id } })
      .then(() => {
        setInstallations((current) => current.map((row) => (row.id === target.id ? { ...row, status: "revoked" } : row)));
        setRevoking(null);
      })
      .catch((err: unknown) => setError(unexpected(err)))
      .finally(() => setBusyId(null));
  };

  const now = new Date();
  const selected = installations.find((row) => row.id === selectedId) ?? null;
  const secretText = (row: Installation) =>
    row.status === "revoked" ? "Revoked" : row.rotationPending ? <Chip tone="amber">Rotation pending</Chip> : "Current";
  const healthChip = (row: Installation) => {
    const chip = HEALTH_CHIPS[installationHealth(row, now)];
    return <Chip tone={chip.tone}>{chip.label}</Chip>;
  };
  const columns: Column<Installation>[] = [
    {
      key: "name",
      header: "Installation",
      cell: (row) => (
        <span class="name-with-id">
          <span>{row.name}</span>
          <CopyId id={row.id} label="" what="installation ID" />
        </span>
      ),
      compare: (a, b) => a.name.localeCompare(b.name),
    },
    {
      key: "account",
      header: "AWS account",
      cell: (row) => (
        <span class="name-with-id">
          <span class="mono">{row.awsAccountId ?? "—"}</span>
          {row.awsRegion && <span class="cell-sub">{row.awsRegion}</span>}
        </span>
      ),
    },
    {
      key: "health",
      header: "Health",
      help: "customers.installationHealth",
      cell: healthChip,
    },
    { key: "lastSeenAt", header: "Last heartbeat", cell: (row) => (row.lastSeenAt ? dayLabel(row.lastSeenAt) : "Never") },
    { key: "pluginVersion", header: "Plugin version", cell: (row) => row.pluginVersion ?? "—" },
    {
      key: "secret",
      header: "Secret",
      help: "customers.secretRotation",
      cell: secretText,
    },
  ];

  const registerButton = (
    <button type="button" class="button button-primary" onClick={() => setOpen(true)}>
      <Icon name="plus" />
      Register installation
    </button>
  );

  return (
    <>
      <Card
        title="Proxy installations"
        help="customers.installations"
        actions={
          <>
            {canInstall && (
              <Link href={installerAccountPath(orgId)} class="button">
                Linked AWS accounts in the Installer
              </Link>
            )}
            {registerButton}
          </>
        }
      >
        <p class="card-note">
          Each installation is one Nexus proxy stack in one of the customer's AWS accounts. Its id is the audience developers give
          nexus-aws-token for AWS sign-in.
          {canInstall && " Upgrade an installation through the Installer."}
        </p>
        <DataTable
          caption="Proxy installations"
          hideCaption
          columns={columns}
          rows={installations}
          rowKey={(row) => row.id}
          loading={loading}
          error={error}
          onRetry={() => load(null, true)}
          emptyMessage="No installations yet — register the first."
          emptyAction={registerButton}
          nextCursor={cursor}
          onLoadMore={() => load(cursor, false)}
          onRowClick={(row) => setSelectedId(row.id)}
          rowLabel={(row) => `Installation ${row.name}`}
        />
      </Card>

      {selected && (
        <RowDetail
          title={selected.name}
          open
          onClose={() => setSelectedId(null)}
          facts={[
            { label: "Installation ID", value: <CopyId id={selected.id} label="" what="installation ID" /> },
            { label: "AWS account", value: <span class="mono">{selected.awsAccountId ?? "—"}</span> },
            { label: "Region", value: selected.awsRegion ?? "—" },
            { label: "Health", value: healthChip(selected) },
            { label: "Last heartbeat", value: selected.lastSeenAt ? dayLabel(selected.lastSeenAt) : "Never" },
            { label: "Plugin version", value: selected.pluginVersion ?? "—" },
            { label: "Secret", value: secretText(selected) },
          ]}
          actions={
            <>
              {canRevokeInstallation(selected) && (
                <button type="button" class="button button-danger" disabled={busyId === selected.id} onClick={() => (setSelectedId(null), setRevoking(selected))}>
                  Revoke
                </button>
              )}
              {canRotateInstallationSecret(selected) && (
                <button type="button" class="button" disabled={busyId === selected.id} onClick={() => (setSelectedId(null), rotate(selected))}>
                  Rotate secret
                </button>
              )}
              {canPromoteInstallationSecret(selected) && (
                <button type="button" class="button" disabled={busyId === selected.id} onClick={() => promote(selected)}>
                  Promote secret
                </button>
              )}
            </>
          }
        />
      )}

      <Dialog
        title="Register installation"
        open={open}
        onClose={closeDialog}
        actions={
          <>
            <button type="button" class="button button-secondary" onClick={closeDialog}>
              Cancel
            </button>
            <button type="button" class="button button-primary" disabled={submitting} onClick={submit}>
              {submitting ? "Registering…" : "Register"}
            </button>
          </>
        }
      >
        <div class="stack">
          <Field id="installation-name" label="Name" help="customers.installationName" error={errors.name}>
            <input
              id="installation-name"
              value={form.name}
              autocomplete="off"
              aria-invalid={errors.name ? true : undefined}
              aria-describedby={errors.name ? errorId("installation-name") : undefined}
              onInput={(event) => setForm({ ...form, name: event.currentTarget.value })}
            />
          </Field>
          <Field id="installation-account" label="AWS account id" help="customers.awsAccountId" error={errors.awsAccountId}>
            <input
              id="installation-account"
              class="input-mono"
              inputMode="numeric"
              value={form.awsAccountId}
              autocomplete="off"
              aria-invalid={errors.awsAccountId ? true : undefined}
              aria-describedby={errors.awsAccountId ? errorId("installation-account") : undefined}
              onInput={(event) => setForm({ ...form, awsAccountId: event.currentTarget.value })}
            />
          </Field>
          <Field id="installation-region" label="AWS region" help="customers.awsRegion">
            <input
              id="installation-region"
              class="input-mono"
              placeholder="us-east-1"
              value={form.awsRegion}
              autocomplete="off"
              onInput={(event) => setForm({ ...form, awsRegion: event.currentTarget.value })}
            />
          </Field>
        </div>
      </Dialog>

      <SecretDialog title="Installation secret" id="installation-secret" secret={secret} onChange={setSecret} />

      <ConfirmDialog
        title="Revoke installation"
        open={revoking !== null}
        message={revoking ? `This immediately stops "${revoking.name}" from authenticating.` : ""}
        confirmLabel="Revoke"
        typedName={revoking?.name ?? ""}
        busy={busyId === revoking?.id}
        onConfirm={confirmRevoke}
        onClose={() => setRevoking(null)}
      />
    </>
  );
}
